import { find, isNil } from "lodash";
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  DEFAULT_TIMEBAND,
  EDIT_ONE_MODE_ACTIVE_WARNING,
  MATRIX_MODES,
  ERROR_UNABLE_TO_SELECT_LEVEL,
  ERROR_AREA_NOT_AVAILABLE,
  ERROR_ANALYZING_MAP,
  POSTCODE,
  ROUTE,
  DRIVER,
  MULTIPLE_ROUTES_EXIST_IN_AREA,
  YOU_HAVE_ELECTED_TO_REMOVE,
  DRIVER_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING,
  SECTION_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING,
  SHIFT_MANAGER,
  SECTION,
  LOCATION,
} from "../../consts/strings";
import { ADD } from "../../consts/map";
import { LAYERS } from "../../consts/layers";
import { DEFAULT_START_TIME_LABEL } from "../../consts/timeSlider";
import { SELECTED_ROUTE_NO_POSTCODES } from "../../consts/messages";
import {
  DRIVER_MANAGEMENT,
  PLAN_MANAGEMENT,
  ROUTE_MANAGEMENT,
  SECTION_MANAGEMENT,
  SHIFT_MANAGER_MANAGEMENT,
} from "../../consts/routes";
import {
  COLLECTIONS_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  NON_DELIVERY_PLAN_TYPES,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
} from "../../consts/planTypes";
import { ACTIVE, INACTIVE, ALL } from "../../consts/driverDetails";

import { isEditMode } from "../../utils/map";
import { getRouteEditOptions } from "../../utils/routeEditOptions";
import { getCollectionEditAction } from "../../utils/collectionEditAction";
import { routeHasTerritory } from "../../utils/routes";
import { Postcode } from "../../utils/postcode";
import { getDriverDayEdits, isDriverActive } from "../../utils/drivers";

import {
  addNotification,
  setSelectedTimeBand,
  setRouteEditOptionsModal,
  setSelectedRoute,
  setSelectedDriver,
  setCollectionEditPostcode,
  setSelectedSection,
  setLoading,
  setSelectedShiftManager,
  setSelectedLocation,
} from ".";
import {
  editArtificialPostcode,
  fetchDepotPartialCodes,
  fetchDepotConfig,
  fetchLookUpTables,
  resetDepot,
} from "../depot/thunkActions";
import {
  fetchMatrixProfiles,
  fetchOverlays,
  lockPlan,
  resetMatrix,
  setMatrixModeThunk,
  processRouteEditAction,
  processCollectionEditAction,
  fetchRobotSuppliers,
  setNoCoordinatesLocation,
} from "../matrixProfile/thunkActions";
import {
  resetDriverManagement,
  fetchDepotMatrixWeek,
  fetchDepotSectionWeek,
  fetchVehicles,
  fetchCountries,
  setDriverView,
  setDriverLocationCoordinates,
} from "../driverManagement/thunkActions";
import {
  resetSectionManagement,
  fetchDepotSectionWeek as fetchSectionManagementDepotSectionWeek,
  setSectionView,
} from "../sectionManagement/thunkActions";
import {
  driverEditSelector,
  driverProfileSelector,
  driverViewSelector,
  matrixDaysSelector,
  selectingDriverLocationSelector,
} from "../driverManagement/selectors";
import {
  fetchDepotRoutes,
  resetRouteManagement,
} from "../routeManagement/thunkActions";
import {
  fetchDepotSections,
  fetchDepotShiftManagers,
  resetShiftManagerManagement,
  setShiftManagerView,
} from "../shiftManagerManagement/thunkActions";
import { routesSelector as depotRoutesSelector } from "../routeManagement/selectors";
import {
  sectionEditSelector,
  sectionSelector,
  sectionViewSelector,
} from "../sectionManagement/selectors";
import {
  getMatrixProfileType,
  matrixProfileLockSelector,
  routeConfigEditSelector,
  routeInEditSelector,
  routesSelector,
  routesByOuterSelector,
  routeInEditCodeSelector,
  redirectConfigSelector,
  collectionAreasSelector,
  postcodeToRouteSelector,
  locationSelector,
} from "../matrixProfile/selectors";
import {
  selectedCollectionTabSelector,
  collectionTimeEditSelector,
  selectedRouteSelector,
  selectedDriverSelector,
  selectedTimeBandSelector,
  selectedTabSelector,
  selectedSectionSelector,
  selectedLocationSelector,
} from "./selectors";
import { setDepotInfo } from "../depot";
import { setFitBounds, setJumpTo } from "../map/thunkActions";
import { zoomLevelSelector } from "../map/selectors";
import {
  artificialPostcodeInEditSelector,
  lookupTableSelector,
} from "../depot/selectors";
import {
  shiftManagerSelector,
  shiftManagerViewSelector,
} from "../shiftManagerManagement/selectors";

export const setSelectedDepot = createAsyncThunk(
  "ownApp/setSelectedDepot",
  async ({ value, application }, { dispatch, getState }) => {
    await dispatch(resetDriverManagement());
    await dispatch(resetRouteManagement());
    await dispatch(resetSectionManagement());
    await dispatch(resetShiftManagerManagement());
    await dispatch(resetMatrix());
    await dispatch(resetDepot());

    if (application === DRIVER_MANAGEMENT) {
      const results = await Promise.all([
        dispatch(fetchLookUpTables(value)),
        dispatch(fetchDepotPartialCodes(value)),
        dispatch(fetchVehicles()),
        dispatch(fetchCountries()),
        dispatch(fetchDepotSectionWeek({ depotCode: value })),
      ]);
      if (results.every(({ error }) => !error))
        dispatch(fetchDepotMatrixWeek({ depotCode: value }));
      else dispatch(setLoading(true));
    } else if (application === ROUTE_MANAGEMENT) {
      const results = await Promise.all([
        dispatch(fetchLookUpTables(value)),
        dispatch(fetchDepotPartialCodes(value)),
      ]);
      if (results.every(({ error }) => !error))
        await dispatch(fetchDepotRoutes({ depotCode: value }));
      else dispatch(setLoading(true));
    } else if (application === SECTION_MANAGEMENT) {
      const results = await Promise.all([
        dispatch(fetchLookUpTables(value)),
        dispatch(fetchDepotPartialCodes(value)),
      ]);
      if (results.every(({ error }) => !error))
        await dispatch(
          fetchSectionManagementDepotSectionWeek({ depotCode: value })
        );
      else dispatch(setLoading(true));
    } else if (application === SHIFT_MANAGER_MANAGEMENT) {
      const results = await Promise.all([
        dispatch(fetchLookUpTables(value)),
        dispatch(fetchDepotPartialCodes(value)),
        dispatch(fetchDepotSections({ depotCode: value })),
      ]);
      if (results.every(({ error }) => !error))
        await dispatch(fetchDepotShiftManagers({ depotCode: value }));
      else dispatch(setLoading(true));
    } else if (application === PLAN_MANAGEMENT) {
      const {
        metadata: { depotAccess },
      } = getState().ownUser;
      const depotInfo = find(depotAccess, ["depotCode", value]);
      dispatch(setDepotInfo(depotInfo));
      await Promise.all([
        dispatch(fetchRobotSuppliers(value)),
        dispatch(fetchDepotConfig(value)),
        dispatch(fetchLookUpTables(value)),
        dispatch(fetchDepotPartialCodes(value)),
        dispatch(fetchMatrixProfiles(value)),
        dispatch(fetchOverlays(value)),
      ]);
    }
    return value;
  }
);

export const setAreaRouteEditAction = createAsyncThunk(
  "ownApp/setAreaRouteEditAction",
  ({ postcodeKey }, { getState, dispatch }) => {
    try {
      const state = getState();
      const routeConfigEdit = routeConfigEditSelector(state);
      const redirectConfig = redirectConfigSelector(state);
      const routeEditCode = routeInEditCodeSelector(state);
      const selectedTimeBand = selectedTimeBandSelector(state);
      const planType = getMatrixProfileType(state);
      const routesByOuter = routesByOuterSelector(state);
      const lookupTables = lookupTableSelector(state);
      const options = getRouteEditOptions({
        postcodeKey,
        routeConfig: routeConfigEdit,
        redirectConfig,
        planType,
        selectedTimeBand,
        routeEditCode,
        routesByOuter,
        lookupTables,
      });
      if (options.length > 1)
        dispatch(
          setRouteEditOptionsModal({
            open: true,
            message: options.every(({ type }) => type === ADD)
              ? MULTIPLE_ROUTES_EXIST_IN_AREA
              : YOU_HAVE_ELECTED_TO_REMOVE(routeEditCode, postcodeKey),
            options,
          })
        );
      else
        dispatch(
          processRouteEditAction({
            change: options[0],
          })
        );
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_ANALYZING_MAP,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const processAreaEdit = createAsyncThunk(
  "ownApp/processAreaEdit",
  async ({ postcodeKey }, { getState, dispatch }) => {
    const state = getState();
    const type = getMatrixProfileType(state);
    const routeEditCode = routeInEditCodeSelector(state);
    const collectionTimeEdit = collectionTimeEditSelector(state);
    const collectionAreas = collectionAreasSelector(state);
    const collectionSelectedTab = selectedTabSelector(state);
    const routesByOuter = routesByOuterSelector(state);
    const lookupTables = lookupTableSelector(state);

    if ([OVERRIDES_PLAN_TYPE, PICKUP_PLAN_TYPE].includes(type))
      dispatch(setSelectedPostcodes(postcodeKey));
    else if (type !== COLLECTIONS_PLAN_TYPE)
      dispatch(setAreaRouteEditAction({ postcodeKey }));
    else if (collectionTimeEdit)
      dispatch(setCollectionEditPostcode(postcodeKey));
    else
      dispatch(
        processCollectionEditAction({
          change: getCollectionEditAction({
            postcodeKey,
            routesByOuter,
            routeEditCode,
            collectionAreas,
            collectionSelectedTab,
            lookupTables,
          }),
        })
      );
  }
);

export const postcodeClickHandler = createAsyncThunk(
  "ownApp/postcodeClickHandler",
  async (
    { postcode, level, lngLat = null, application },
    { dispatch, getState }
  ) => {
    const state = getState();
    const zoomLevel = zoomLevelSelector(state);
    const routes = routesSelector(state);
    const routeEdit = routeInEditSelector(state);
    const type = getMatrixProfileType(state);
    const collectionTimeEdit = collectionTimeEditSelector(state);
    const selectedRoute = selectedRouteSelector(state);
    const postcodeToRoute = postcodeToRouteSelector(state);
    const selectingDriverLocation = selectingDriverLocationSelector(state);

    if (application === DRIVER_MANAGEMENT) {
      return (
        selectingDriverLocation &&
        dispatch(setDriverLocationCoordinates({ postcode, level, lngLat }))
      );
    } else if (
      isEditMode({ routeEdit, collectionTimeEdit, type }) &&
      type !== LOCATIONS_PLAN_TYPE
    )
      return dispatch(processAreaEdit({ postcodeKey: postcode }));
    else if (
      [LOCATIONS_PLAN_TYPE, OVERRIDES_PLAN_TYPE].includes(type) ||
      application !== PLAN_MANAGEMENT
    )
      return;
    const routeCode = postcodeToRoute?.[zoomLevel]?.[postcode];
    if (!routeCode || routeCode === selectedRoute?.routeCode) return;
    const newSelectedRoute = routes.find(
      (route) => route.routeCode === routeCode
    );
    newSelectedRoute && dispatch(setSelectedRoute(newSelectedRoute));
  }
);

export const locationClickHandler = createAsyncThunk(
  "ownApp/locationClickHandler",
  async ({ locationCode }, { dispatch, getState }) => {
    const state = getState();
    const routes = routesSelector(state);
    const selectedRoute = selectedRouteSelector(state);
    const selectedLocation = selectedLocationSelector(state);
    const location = locationSelector(locationCode)(state);

    if (locationCode !== selectedLocation?.locationCode)
      dispatch(setSelectedLocation(location));

    const { routeCode } = location || {};
    if (routeCode && routeCode !== selectedRoute?.routeCode) {
      const newSelectedRoute = routes.find(
        (route) => route.routeCode === routeCode
      );
      newSelectedRoute && dispatch(setSelectedRoute(newSelectedRoute));
    }
  }
);

export const mapClickHandler = createAsyncThunk(
  "ownApp/mapClickHandler",
  async ({ map, point, lngLat, application }, { getState, dispatch }) => {
    const state = getState();
    const zoomLevel = zoomLevelSelector(state);
    const routeEdit = routeInEditSelector(state);
    const artificialPostcodeEdit = artificialPostcodeInEditSelector(state);
    const [feature] = map.queryRenderedFeatures(point, {
      layers: [LAYERS[zoomLevel].BOUNDARIES.NAME],
    });
    const postcodeKey = feature?.state?.root || feature?.state?.outer;

    if (!!artificialPostcodeEdit) dispatch(editArtificialPostcode({ lngLat }));

    if (!feature) return;
    else if (!postcodeKey) {
      dispatch(
        addNotification({
          message: ERROR_AREA_NOT_AVAILABLE,
          options: { variant: "error" },
        })
      );
      dispatch(setSelectedRoute(routeEdit || null));
    } else
      dispatch(
        postcodeClickHandler({
          postcode: postcodeKey,
          level: zoomLevel,
          lngLat,
          application,
        })
      );
  }
);

export const setRouteWithTimeband = createAsyncThunk(
  "ownApp/setRouteWithTimeband",
  (route, { getState, dispatch }) => {
    const state = getState();
    const planType = getMatrixProfileType(state);
    const collectionType = selectedCollectionTabSelector(state);
    const { selectedTimeBand } = state.ownApp;

    const plannedReadyTime = route.plannedReadyTimeAsString ?? DEFAULT_TIMEBAND;
    if (
      !NON_DELIVERY_PLAN_TYPES.includes(planType) &&
      routeHasTerritory(route, planType, collectionType) &&
      selectedTimeBand !== plannedReadyTime
    ) {
      dispatch(setSelectedTimeBand(plannedReadyTime));
    }
    dispatch(setSelectedRoute(route));
  }
);

export const onRouteCLick = createAsyncThunk(
  "ownApp/onRouteCLick",
  async (route, { getState, dispatch }) => {
    const state = getState();
    const planType = getMatrixProfileType(state);
    const collectionType = selectedCollectionTabSelector(state);
    const { routeEdit, matrixMode } = state.matrixProfile;
    const isEditOneMode = matrixMode === MATRIX_MODES.EDIT_ONE_ROUTE;

    if (isEditOneMode && route.routeCode !== routeEdit?.routeCode) {
      return dispatch(
        addNotification({
          message: EDIT_ONE_MODE_ACTIVE_WARNING(routeEdit?.routeCode),
          options: { variant: "info" },
        })
      );
    } else if (isEditOneMode) return;

    const { selectedRoute, selectedTimeBand } = getState().ownApp;

    if (selectedRoute && route.routeCode === selectedRoute.routeCode) {
      dispatch(setSelectedRoute(null));
      return;
    }
    if (!routeHasTerritory(route, planType, collectionType)) {
      dispatch(
        addNotification({
          message: SELECTED_ROUTE_NO_POSTCODES,
          options: { variant: "info" },
        })
      );
    }

    const plannedReadyTime = route.plannedReadyTimeAsString || DEFAULT_TIMEBAND;
    if (
      !NON_DELIVERY_PLAN_TYPES.includes(planType) &&
      selectedTimeBand !== plannedReadyTime
    ) {
      dispatch(setSelectedTimeBand(plannedReadyTime));
    }
    dispatch(setSelectedRoute(route));
  }
);

export const onDriverClick = createAsyncThunk(
  "ownApp/onDriverClick",
  async ({ driverCode }, { getState, dispatch }) => {
    const state = getState();
    const selectedDriver = selectedDriverSelector(state);
    const driverProfile = driverProfileSelector(driverCode)(state);
    const matrixDays = matrixDaysSelector(state);
    const driverEdit = driverEditSelector(state);

    if (driverEdit)
      dispatch(
        addNotification({
          message: DRIVER_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(
            driverEdit.driverProfileEdit
          ),
          options: { variant: "info" },
        })
      );
    else if (driverCode === selectedDriver?.driverCode)
      dispatch(setSelectedDriver(null));
    else {
      dispatch(
        setSelectedDriver({
          driverCode,
          driverProfileEdit: { ...driverProfile },
          driverDayEdits: getDriverDayEdits({
            driverCode,
            matrixDays,
          }),
        })
      );
    }
  }
);

export const onSectionClick = createAsyncThunk(
  "ownApp/onSectionClick",
  async ({ sectionCode }, { getState, dispatch }) => {
    const state = getState();
    const selectedSection = selectedSectionSelector(state);
    const sectionEdit = sectionEditSelector(state);
    const section = sectionSelector(sectionCode)(state);

    if (sectionEdit)
      dispatch(
        addNotification({
          message: SECTION_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(section),
          options: { variant: "info" },
        })
      );
    else if (sectionCode === selectedSection?.sectionCode)
      dispatch(setSelectedSection(null));
    else {
      dispatch(setSelectedSection(section));
    }
  }
);

export const searchHandler = createAsyncThunk(
  "ownApp/searchHandler",
  ({ item, type, application }, { dispatch, getState }) => {
    const state = getState();
    if (type === DRIVER) {
      const { driverCode } = item;
      const driverView = driverViewSelector(state);
      const driverProfile = driverProfileSelector(driverCode)(state);
      const matrixDays = matrixDaysSelector(state);
      const active = isDriverActive({ driverProfile, matrixDays });
      if (active ? driverView === INACTIVE : driverView === ACTIVE)
        dispatch(setDriverView(ALL));
      return dispatch(onDriverClick(driverProfile));
    } else if (type === SECTION) {
      const { sectionCode } = item;
      const sectionView = sectionViewSelector(state);
      const section = sectionSelector(sectionCode)(state);
      if (section.isActive ? sectionView === INACTIVE : sectionView === ACTIVE)
        dispatch(setSectionView(ALL));
      return dispatch(setSelectedSection(section));
    } else if (type === SHIFT_MANAGER) {
      const { shiftManagerCode } = item;
      const shiftManagerView = shiftManagerViewSelector(state);
      const shiftManager = shiftManagerSelector(shiftManagerCode)(state);
      if (
        shiftManager.isActive
          ? shiftManagerView === INACTIVE
          : shiftManagerView === ACTIVE
      )
        dispatch(setShiftManagerView(ALL));
      return dispatch(setSelectedShiftManager(shiftManager));
    }

    const lookupTables = state.depot.lookupTables;

    if (type === POSTCODE && lookupTables) {
      const { bbox, center } = lookupTables[item.type][item.value];
      if (!!bbox) dispatch(setFitBounds({ bbox, targetZoomLevel: item.type }));
      else if (!!center)
        dispatch(setJumpTo({ center, targetZoomLevel: item.type }));
    } else if (type === LOCATION && lookupTables) {
      const { locationCode } = item;
      const location = locationSelector(locationCode)(state);
      const { latitude, longitude } = location || {};
      if (!isNil(latitude) && !isNil(longitude))
        dispatch(
          setJumpTo({
            center: [longitude, latitude],
            zoom: 16,
          })
        );
      else dispatch(setNoCoordinatesLocation(location.locationCode));
    } else if (type === ROUTE && application === PLAN_MANAGEMENT) {
      const routes = state.matrixProfile.routes;
      const selectedRoute = routes.find(
        (route) => route.routeCode === item.routeCode
      );
      dispatch(onRouteCLick(selectedRoute));
    } else if (type === ROUTE && application === ROUTE_MANAGEMENT) {
      const routes = depotRoutesSelector(state);
      const selectedRoute = routes.find(
        (route) => route.routeCode === item.routeCode
      );
      dispatch(onRouteCLick(selectedRoute));
    }
  }
);

export const toggleFinalRoutesMode = createAsyncThunk(
  "ownApp/setFinalRoutesMode",
  (payload, { dispatch, getState }) => {
    const { selectedTimeBand, previousSelectedTimeBand } = getState().ownApp;
    let newSelectedTimeBand = null;

    if (selectedTimeBand !== DEFAULT_TIMEBAND) {
      newSelectedTimeBand = DEFAULT_TIMEBAND;
    } else {
      if (
        !!previousSelectedTimeBand &&
        previousSelectedTimeBand !== DEFAULT_TIMEBAND
      ) {
        newSelectedTimeBand = previousSelectedTimeBand;
      } else {
        newSelectedTimeBand = DEFAULT_START_TIME_LABEL;
      }
    }

    dispatch(setSelectedTimeBand(newSelectedTimeBand));
  }
);

export const enableCollectionTimesEdit = createAsyncThunk(
  "ownApp/enableCollectionTimesEdit",
  async (_, { dispatch, getState }) => {
    const state = getState();
    const matrixProfileLock = matrixProfileLockSelector(state);
    if (!matrixProfileLock.lockedByYou) {
      const { error } = await dispatch(lockPlan());
      if (error) throw error;
    }
    dispatch(setMatrixModeThunk(MATRIX_MODES.COLLECTION_TIMES_EDIT));
    return true;
  }
);

export const setSelectedPostcodes = createAsyncThunk(
  "ownApp/setSelectedPostcodes",
  (postcode, { dispatch, getState }) => {
    const selectedPostcodes = [...getState().ownApp.selectedPostcodes];
    const selectedLevel =
      selectedPostcodes?.length && Postcode.getLevel(selectedPostcodes[0]);
    const postcodeLevel = Postcode.getLevel(postcode);
    if (selectedLevel && selectedLevel !== postcodeLevel) {
      dispatch(
        addNotification({
          message: ERROR_UNABLE_TO_SELECT_LEVEL(selectedLevel, postcodeLevel),
          options: { variant: "error" },
        })
      );
      dispatch(
        searchHandler({
          item: {
            type: selectedLevel,
            value: selectedPostcodes[0],
          },
          type: POSTCODE,
        })
      );
    } else if (selectedPostcodes.includes(postcode))
      selectedPostcodes.splice(selectedPostcodes.indexOf(postcode), 1);
    else selectedPostcodes.push(postcode);
    return selectedPostcodes;
  }
);
